import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { Lock, ShoppingCart } from "lucide-react";
import AnimatedLogo from "../components/AnimatedLogo";
import TableClosedModal from "../components/TableClosedModal";
import TableWaitingModal from "../components/TableWaitingModal";
import SessionTracker from "../components/SessionTracker";
import CartaConCarrito from "../components/CartaConCarrito";
import { money } from "../lib/cartaHelpers";
import "./Menu.css";
import "./TableMenu.css";

function TableCartaHeader({ table, cartCount, cartTotal, onCartOpen, isActive }) {
  return (
    <header className="tc-header">
      <div className="tc-header-inner">
        <div className="tc-mesa-info">
          <span className="tc-mesa-label">Mesa</span>
          <span className="tc-mesa-num">{table?.number ?? "—"}</span>
          {!isActive && <span className="tc-mesa-badge">Inactiva</span>}
        </div>
        <AnimatedLogo className="tc-logo" />
        <button
          className={`tc-cart-btn${cartCount > 0 ? " has-items" : ""}`}
          onClick={onCartOpen}
          aria-label={`Abrir carrito — ${cartCount} productos`}
          disabled={cartCount === 0}
        >
          <ShoppingCart size={20} />
          {cartCount > 0 && <span className="tc-cart-badge">{cartCount}</span>}
          {cartCount > 0 && <span className="tc-cart-total">{money(cartTotal)}</span>}
        </button>
      </div>
    </header>
  );
}

export default function TableMenu() {
  const { token } = useParams();

  const [products, setProducts] = useState([]);
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [requestCancelling, setRequestCancelling] = useState(false);
  const [requestRejected, setRequestRejected] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const previousStatusRef = useRef(null);

  const [cartSummary, setCartSummary] = useState({ cart: [], cartCount: 0, cartTotal: 0 });
  const [cartOpenSignal, setCartOpenSignal] = useState(0);

  const refreshAll = useCallback(() => {
    setLoading(true);
    return Promise.all([
      fetch(`/api/tables/token/${token}`).then(response => {
        if (!response.ok) throw new Error("Mesa no encontrada");
        return response.json();
      }),
      fetch("/api/products/public").then(response => response.json()).then(data => Array.isArray(data) ? data : []),
    ])
      .then(([nextTable, nextProducts]) => {
        setTable(nextTable);
        setProducts(nextProducts);
        setError("");
        return nextTable;
      })
      .catch(err => {
        setError(err.message || "Error de conexión");
        return null;
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const tableStatus = table?.status || "inactive";
  const isOpen = tableStatus === "open" && (table?.active === true || table?.active === 1);
  const isWaiting = tableStatus === "open_requested";
  const isClosed = !isOpen && !isWaiting;
  const isActive = isOpen;

  useEffect(() => {
    if (!isWaiting) {
      previousStatusRef.current = tableStatus;
      return;
    }
    previousStatusRef.current = "open_requested";
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch(`/api/tables/token/${token}/open-request-status`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        if (data.status === "open" && previousStatusRef.current === "open_requested") {
          previousStatusRef.current = "open";
          await refreshAll();
          setReadOnlyMode(false);
          return;
        }
        if (data.status === "inactive" && previousStatusRef.current === "open_requested") {
          previousStatusRef.current = "inactive";
          setRequestRejected(true);
          setReadOnlyMode(false);
          await refreshAll();
          return;
        }
        if (data.status === "open_requested" && data.openRequest) {
          setTable(prev => prev ? { ...prev, openRequest: data.openRequest, status: data.status } : prev);
        }
      } catch (_) {
        // El siguiente polling reintentará.
      }
    };
    const id = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isWaiting, tableStatus, token, refreshAll]);

  const handleRequestOpen = useCallback(async (payload) => {
    setRequestSubmitting(true);
    setRequestError("");
    setRequestRejected(false);
    try {
      const res = await fetch(`/api/tables/token/${token}/request-open`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || "No se pudo enviar la solicitud");
      await refreshAll();
      setReadOnlyMode(false);
    } catch (err) {
      setRequestError(err.message || "Error de conexión");
    } finally {
      setRequestSubmitting(false);
    }
  }, [token, refreshAll]);

  const handleCancelRequest = useCallback(async () => {
    setRequestCancelling(true);
    try {
      await fetch(`/api/tables/token/${token}/open-request`, { method: "DELETE" });
      await refreshAll();
    } catch (_) {
      // El polling sincronizará el estado.
    } finally {
      setRequestCancelling(false);
    }
  }, [token, refreshAll]);

  const handleViewMenu = useCallback(() => {
    setReadOnlyMode(true);
    setRequestRejected(false);
  }, []);

  const handleReopenRequest = useCallback(() => {
    setReadOnlyMode(false);
    setRequestRejected(false);
  }, []);

  const aperitivoExtras = useMemo(
    () => products.filter(product => {
      const price = Number(product.price);
      return product.category === "aperitivos" &&
        product.active !== false &&
        price > 0 &&
        product.name !== "Tapa Extra";
    }),
    [products]
  );

  const handleSendOrder = useCallback(async (cartItems) => {
    const res = await fetch(`/api/orders/table/${token}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: cartItems.map(item => ({
          productId: item.productId || String(item.id).split("__")[0],
          variantIndex: item.variantIndex !== null && item.variantIndex !== undefined ? item.variantIndex : undefined,
          aperitivoId: item.aperitivoId || undefined,
          tapaExtraId: item.tapaExtraId || undefined,
          quantity: item.qty,
          notes: item.notes || undefined
        }))
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || "Error al enviar el pedido");
    }
  }, [token]);

  if (loading) return (
    <div className="carta-page">
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
        <p style={{ fontFamily:"Orbitron,monospace", color:"#9ffbff", letterSpacing:"4px", fontSize:"12px" }}>CARGANDO CARTA...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="carta-page">
      <div className="carta-status carta-status-error" style={{ margin:"4rem 1rem" }}>
        <span /><h2>Error</h2><p>{error}</p>
      </div>
    </div>
  );

  return (
    <div className="carta-page tc-root">
      <TableCartaHeader
        table={table}
        cartCount={cartSummary.cartCount}
        cartTotal={cartSummary.cartTotal}
        onCartOpen={() => setCartOpenSignal(value => value + 1)}
        isActive={isActive}
      />

      {readOnlyMode && (
        <motion.div
          className={`tc-readonly-banner${isWaiting ? " is-waiting" : ""}`}
          initial={{ y: -32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", damping: 22, stiffness: 320 }}
        >
          <Lock size={14} />
          <span>
            {isWaiting
              ? "Solicitud enviada — esperando al personal"
              : "Mesa cerrada — modo solo lectura"}
          </span>
          <button onClick={handleReopenRequest}>
            {isWaiting ? "Ver mi solicitud" : "Solicitar apertura"}
          </button>
        </motion.div>
      )}

      <CartaConCarrito
        products={products}
        aperitivoExtras={aperitivoExtras}
        isActive={isActive}
        onSendOrder={handleSendOrder}
        onCartChange={setCartSummary}
        cartOpenSignal={cartOpenSignal}
      />

      {isOpen && (
        <SessionTracker
          token={token}
          isOpen={isOpen}
          onSessionClosed={() => refreshAll()}
          cart={cartSummary.cart}
          cartCount={cartSummary.cartCount}
          cartTotal={cartSummary.cartTotal}
          onOpenCart={() => setCartOpenSignal(value => value + 1)}
        />
      )}

      <AnimatePresence>
        {isClosed && !readOnlyMode && table && (
          <TableClosedModal
            key="closed"
            table={table}
            isSubmitting={requestSubmitting}
            error={requestError}
            rejected={requestRejected}
            rejectReason={rejectReason}
            onRequestOpen={handleRequestOpen}
            onViewMenu={handleViewMenu}
          />
        )}
        {isWaiting && !readOnlyMode && table?.openRequest && (
          <TableWaitingModal
            key="waiting"
            table={table}
            openRequest={table.openRequest}
            isCancelling={requestCancelling}
            onViewMenu={handleViewMenu}
            onCancel={handleCancelRequest}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
